import moment from "moment";
import * as React from "react";
import { getCommunes } from "../api/returnInwardApi";
import type { MasterOrg } from "../api/returnInwardApi";
import { MAX_DESCRIPTION_LENGTH, TABLE_HEAD_HEIGHT, TOP_BAR_HEIGHT } from "../config/returnKinds";
import type { ReturnInwardHeader, ReturnOrderInfo, WarehouseOption } from "../types";
import FieldSelect from "./FieldSelect";
import ReturnInwardHeading from "./ReturnInwardHeading";

/** id của ô chọn kho, để "Chọn kho" trong hộp lỗi đưa focus tới */
export const WAREHOUSE_SELECT_ID = "return-inward-warehouse";

interface ReturnInfoPanelProps {
  kindLabel: string;
  /** true: chọn kho nhập ở đây; false: kho theo phiếu, chỉ đọc */
  pickWarehouse: boolean;
  header: ReturnInwardHeader;
  order: ReturnOrderInfo;
  masterOrgs: MasterOrg[];
  warehouses: WarehouseOption[];
  serialStatuses: string[];
  editDisabled: boolean;
  hasRows: boolean;
  onChangeWarehouse: (invId: number | null) => void;
  onChangeDescription: (value: string) => void;
  onApplyStatus: (status: string) => void;
  /** Cột bên phải ghép sát khối này (không khoảng cách): vuông 2 góc bên phải */
  joinedRight?: boolean;
}

/** Thông tin giao vận lưu dạng JSON trong attribute3 của đơn */
interface DeliveryInfo {
  customerCommuneId?: string | number | null;
}

interface CommuneResult {
  id: string;
  province: string;
  ward: string;
}

const C = {
  card: "#FFFFFF",
  border: "#DDE0DA",
  divider: "#ECEEE9",
  headBg: "#F6F7F4",
  text: "#1B1F24",
  textSub: "#5C6570",
  label: "#6B737C",
  muted: "#8A929B",
  empty: "#A3AAB2",
  accent: "#1F5FAE",
  focusRing: "#E3ECF7",
  inputBorder: "#D5D9D2",
  danger: "#B42318",
  dangerBg: "#FDF1F0",
};

const S: Record<string, React.CSSProperties> = {
  section: {
    position: "relative",
    width: 472,
    maxWidth: "100%",
    maxHeight: "100%",
    flexShrink: 0,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    color: C.text,
    fontSize: 13,
    lineHeight: "normal",
  },
  // Cao bằng thanh Bên nhận bên phải (gồm viền dưới) để hàng Bên xuất / Bên nhận thẳng hàng tiêu đề lưới serial
  heading: {
    flexShrink: 0,
    boxSizing: "border-box",
    minHeight: TOP_BAR_HEIGHT,
    display: "flex",
    alignItems: "center",
    padding: "8px 16px",
    borderBottom: `1px solid ${C.divider}`,
  },
  body: { flex: "1 1 auto", minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column" },
  sides: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    // Bằng hàng tiêu đề AG Grid: chiều cao cấu hình + 1px viền dưới
    boxSizing: "border-box",
    height: TABLE_HEAD_HEIGHT + 1,
    borderBottom: `1px solid ${C.divider}`,
  },
  sideHead: {
    display: "flex",
    alignItems: "center",
    padding: "0 16px",
    background: C.headBg,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: C.textSub,
  },
  rows: { margin: 0, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
  cell: { padding: "8px 16px", minWidth: 0 },
  cellRight: { padding: "8px 16px", minWidth: 0, borderLeft: `1px solid ${C.divider}` },
  field: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
  dt: { color: C.label, fontSize: 12, fontWeight: 400 },
  dd: { margin: 0, fontWeight: 500, color: C.text, overflowWrap: "anywhere" },
  footer: { padding: "14px 16px", borderTop: `1px solid ${C.divider}`, display: "flex", flexDirection: "column", gap: 12 },
  box: { display: "flex", flexDirection: "column", gap: 6 },
  label: { color: C.label, fontSize: 12 },
  error: { color: C.danger, fontSize: 12 },
};

const VISUALLY_HIDDEN: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

/** Ngày giờ dạng 05/10/2026 09:42; ngày trống hoặc năm trước 2000 (mặc định của backend) để trống */
const formatDateTime = (value: string | Date | null | undefined) => {
  if (!value) return "";
  const date = moment(value);
  return date.isValid() && date.year() >= 2000 ? date.format("DD/MM/YYYY HH:mm") : "";
};

const toText = (value: unknown) => (value == null ? "" : String(value).trim());

/** Ghép mã và tên, bỏ phần trống */
const joinParts = (...parts: unknown[]) => parts.map(toText).filter(Boolean).join(" · ");

const parseDeliveryInfo = (raw: unknown): DeliveryInfo | null => {
  if (typeof raw !== "string" || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as DeliveryInfo) : null;
  } catch {
    return null;
  }
};

/** attribute5 là thông tin người mua dạng "tên - sđt"; trống hoặc "0" thì dùng khách hàng của đơn (như trang cũ) */
const parseBuyer = (raw: unknown) => {
  const text = toText(raw);
  if (!text || text === "0") return null;
  const parts = text.split(" - ");
  return { name: parts[0]?.trim() || "", phone: parts[1]?.trim() || "" };
};

const getDeliveryText = (delivery: string) => {
  switch (delivery) {
    case "KHONG":
      return "Nhận tại cửa hàng";
    case "NOI_BO":
    case "HA_COM":
      return "Nội bộ";
    default:
      return delivery;
  }
};

/** Id phường xã mới như trang cũ: đơn có ĐVTN lấy trong attribute3, sau đó id xã mới, rồi id xã cũ */
const pickCommuneId = (order: ReturnOrderInfo, delivery: DeliveryInfo | null) => {
  if (order.dvtn) return toText(delivery?.customerCommuneId);
  if (order.newCommuneId) return toText(order.newCommuneId);
  if (order.cusCommuneId) return toText(order.cusCommuneId);
  return "";
};

interface FieldProps {
  label: React.ReactNode;
  value: string;
  multiline?: boolean;
  pending?: boolean;
}

/** Một cặp nhãn nhỏ ở trên, giá trị ở dưới; trống thì hiện "—" */
const Field = ({ label, value, multiline, pending }: FieldProps) => (
  <div style={S.field}>
    <dt style={S.dt}>{label}</dt>
    <dd style={{ ...S.dd, lineHeight: multiline ? 1.45 : undefined }}>
      {value || <span style={{ color: pending ? C.muted : C.empty }}>{pending ? "Đang tải…" : "—"}</span>}
    </dd>
  </div>
);

const noteStyle = (focused: boolean, invalid: boolean, disabled: boolean): React.CSSProperties => ({
  width: "100%",
  boxSizing: "border-box",
  font: "inherit",
  resize: "none",
  border: `1px solid ${invalid ? C.danger : focused ? C.accent : C.inputBorder}`,
  borderRadius: 8,
  padding: "8px 10px",
  outline: "none",
  boxShadow: focused ? `0 0 0 3px ${invalid ? C.dangerBg : C.focusRing}` : "none",
  background: disabled ? C.headBg : C.card,
  color: disabled ? C.textSub : C.text,
  cursor: disabled ? "not-allowed" : undefined,
});

/**
 * Khối "Thông tin chung" của phiếu nhập đổi trả: bên xuất (khách trả hàng), bên nhận (chi nhánh, kho),
 * diễn giải, tình trạng áp cho mọi serial và ghi chú phiếu. Kho nhập chọn ngay trong khối này như trang cũ.
 */
const ReturnInfoPanel = ({
  kindLabel,
  pickWarehouse,
  header,
  order,
  masterOrgs,
  warehouses,
  serialStatuses,
  editDisabled,
  hasRows,
  onChangeWarehouse,
  onChangeDescription,
  onApplyStatus,
  joinedRight = false,
}: ReturnInfoPanelProps) => {
  const titleId = React.useId();
  const noteId = React.useId();
  const noteErrorId = React.useId();
  const statusId = React.useId();
  const [noteFocused, setNoteFocused] = React.useState(false);
  const [statusAll, setStatusAll] = React.useState("");
  const [commune, setCommune] = React.useState<CommuneResult | null>(null);

  const delivery = React.useMemo(() => parseDeliveryInfo(order.attribute3), [order.attribute3]);
  const buyer = parseBuyer(order.attribute5);
  const communeId = pickCommuneId(order, delivery);

  // Tra tỉnh / phường xã mới theo id xã; id đổi thì bỏ kết quả của lần tra trước
  React.useEffect(() => {
    if (!communeId) return undefined;
    let cancelled = false;
    getCommunes(communeId)
      .then((res) => {
        if (cancelled) return;
        const first = res?.success && Array.isArray(res.data) ? res.data[0] : undefined;
        setCommune({ id: communeId, province: toText(first?.att1), ward: toText(first?.text) });
      })
      .catch(() => {
        if (!cancelled) setCommune({ id: communeId, province: "", ward: "" });
      });
    return () => {
      cancelled = true;
    };
  }, [communeId]);

  const currentCommune = commune?.id === communeId ? commune : null;
  const communePending = !!communeId && !currentCommune;

  const orgCode = masterOrgs.find((org) => String(org.orgId) === String(order.toBranchId))?.orgCode;
  const description = header.description ?? "";
  const noteTooLong = description.length > MAX_DESCRIPTION_LENGTH;
  const noteActive = noteFocused && !editDisabled;

  const warehouseId = header.subInvId ? String(header.subInvId) : "";
  // Kho đã lưu có thể không còn trong danh sách lọc (đổi mã kho...): vẫn hiện để không mất giá trị
  const warehouseOptions = React.useMemo(() => {
    const options = warehouses.map((inv) => ({
      value: String(inv.inventoryId),
      label: joinParts(inv.inventoryCode, inv.inventoryName),
      primary: toText(inv.inventoryCode),
      hint: toText(inv.inventoryName),
    }));
    if (warehouseId && !options.some((option) => option.value === warehouseId)) {
      options.unshift({
        value: warehouseId,
        label: joinParts(header.toInvCode, header.toInvName) || warehouseId,
        primary: toText(header.toInvCode) || warehouseId,
        hint: toText(header.toInvName),
      });
    }
    return options;
  }, [warehouses, warehouseId, header.toInvCode, header.toInvName]);

  const warehouseLabel = (
    <label htmlFor={pickWarehouse ? WAREHOUSE_SELECT_ID : undefined}>
      Kho nhập{pickWarehouse && <b style={{ color: C.danger, marginLeft: 1 }}>*</b>}
    </label>
  );

  const warehouseCell = pickWarehouse ? (
    <div style={S.field}>
      <dt style={S.dt}>{warehouseLabel}</dt>
      <dd style={{ ...S.dd, marginTop: 2 }}>
        <FieldSelect
          id={WAREHOUSE_SELECT_ID}
          value={warehouseId}
          placeholder="Chọn kho nhập"
          options={warehouseOptions}
          disabled={editDisabled}
          needed={!editDisabled && !warehouseId}
          height={34}
          dropdownWidth={300}
          title={warehouseOptions.find((option) => option.value === warehouseId)?.label}
          onChange={(value) => onChangeWarehouse(value ? Number(value) : null)}
        />
      </dd>
    </div>
  ) : (
    <Field label="Kho nhập" value={joinParts(header.toInvCode ?? order.toInvCode, header.toInvName ?? order.toInvName)} />
  );

  // Mỗi hàng một cặp Bên xuất (khách trả) / Bên nhận (chi nhánh, kho), giống phiếu in
  const rows: [React.ReactNode, React.ReactNode][] = [
    [
      <Field label="Mã số khách hàng" value={toText(order.cusCode)} />,
      <Field label="Thời gian tạo" value={formatDateTime(order.createDate ?? header.createDate)} />,
    ],
    [
      <Field label="Khách hàng" value={toText(buyer ? buyer.name : order.cusName)} />,
      <Field label="Thời gian hoàn thành" value={formatDateTime(header.finishDate)} />,
    ],
    [
      <Field label="Số điện thoại" value={toText(buyer ? buyer.phone : order.cusTelephone)} />,
      <Field label="Mã phiếu nhập kho" value={toText(header.transactionCode)} />,
    ],
    [
      <Field
        label="Tỉnh/Thành"
        value={currentCommune?.province || toText(order.cusProvinceName)}
        pending={communePending}
      />,
      <Field label="Số chứng từ (đơn đổi trả)" value={toText(header.sourceCode)} />,
    ],
    [
      <Field label="Phường/Xã" value={currentCommune?.ward || toText(order.cusCommuneName)} pending={communePending} />,
      <Field label="Chi nhánh" value={joinParts(orgCode, order.toBranchName)} />,
    ],
    [<Field label="Địa chỉ cụ thể" value={toText(order.cusAddress)} multiline />, warehouseCell],
    [
      <Field label="Hình thức giao" value={getDeliveryText(toText(order.delivery))} />,
      <Field label="Địa điểm làm việc" value={toText(order.toBranchAddress)} multiline />,
    ],
    [null, <Field label="Người lập" value={order.createName ? joinParts(order.createName, order.createCode) : ""} />],
    [null, <Field label="Chức danh" value={toText(order.createRoleName)} />],
  ];

  const statusDisabled = editDisabled || !hasRows;

  return (
    <section
      aria-labelledby={titleId}
      style={joinedRight ? { ...S.section, borderTopRightRadius: 0, borderBottomRightRadius: 0 } : S.section}
    >
      <h2 id={titleId} style={VISUALLY_HIDDEN}>
        Thông tin chung
      </h2>
      <div style={S.heading}>
        <ReturnInwardHeading kindLabel={kindLabel} status={header.status} />
      </div>
      <div style={S.body}>
        <div style={S.sides}>
          <div style={S.sideHead}>Bên xuất</div>
          <div style={{ ...S.sideHead, borderLeft: `1px solid ${C.divider}` }}>Bên nhận</div>
        </div>
        <dl style={S.rows}>
          {rows.map(([left, right], index) => {
            const rowLine = index < rows.length - 1 ? `1px solid ${C.divider}` : undefined;
            return (
              <React.Fragment key={index}>
                <div style={{ ...S.cell, borderBottom: rowLine }}>{left}</div>
                <div style={{ ...S.cellRight, borderBottom: rowLine }}>{right}</div>
              </React.Fragment>
            );
          })}
        </dl>
        <div style={S.footer}>
          <dl style={{ margin: 0 }}>
            <Field label="Diễn giải chứng từ gốc" value={toText(header.sourceDescription ?? order.sourceDescription)} multiline />
          </dl>
          <div style={S.box}>
            <label htmlFor={statusId} style={S.label}>
              Tình trạng cho tất cả serial
            </label>
            <FieldSelect
              id={statusId}
              value={statusAll}
              placeholder="Chọn để áp cho mọi serial"
              options={serialStatuses.map((status) => ({ value: status, label: status }))}
              disabled={statusDisabled}
              height={36}
              onChange={(value) => {
                setStatusAll(value);
                if (value) onApplyStatus(value);
              }}
            />
          </div>
          <div style={S.box}>
            <label htmlFor={noteId} style={S.label}>
              Ghi chú phiếu
            </label>
            <textarea
              id={noteId}
              rows={2}
              value={description}
              placeholder={editDisabled ? undefined : "Vui lòng nhập"}
              disabled={editDisabled}
              aria-invalid={noteTooLong || undefined}
              aria-describedby={noteTooLong ? noteErrorId : undefined}
              onChange={(e) => onChangeDescription(e.target.value)}
              onFocus={() => setNoteFocused(true)}
              onBlur={() => setNoteFocused(false)}
              style={noteStyle(noteActive, noteTooLong, editDisabled)}
            />
            {noteTooLong && (
              <div id={noteErrorId} style={S.error}>
                Ghi chú không được vượt quá 300 kí tự!
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default React.memo(ReturnInfoPanel);
